import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  FaUsers, FaUserTie, FaSearch, FaFilter, FaMapMarkerAlt,
  FaStar, FaCheck, FaCertificate, FaDollarSign, FaCalendarAlt,
  FaGlobe, FaPhone, FaEnvelope, FaArrowLeft, FaBriefcase, FaAward
} from 'react-icons/fa';
import axios from 'axios';
import { useAuth, axiosInstance } from '../context/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { RequestProButton } from '../components/professionals/RequestProButton';
import { ComingSoonAdvisors } from '../components/professionals/ComingSoonAdvisors';
import { ProfileDetails } from '../components/professionals/ProfileDetails';
import { ShareProfileLink } from '../components/professionals/ShareProfileLink';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const PROFESSIONAL_TYPES = {
  financial_advisor: { label: 'Financial Advisor', icon: '💼', color: 'blue' },
  estate_attorney: { label: 'Estate Attorney', icon: '⚖️', color: 'purple' },
  cpa_tax: { label: 'CPA & Tax Expert', icon: '📊', color: 'green' },
  real_estate: { label: 'Real Estate Expert', icon: '🏠', color: 'amber' },
  insurance: { label: 'Insurance Specialist', icon: '🛡️', color: 'red' },
  family_therapist: { label: 'Family Therapist', icon: '❤️', color: 'pink' }
};

export default function ProfessionalDirectory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filterType, setFilterType] = useState(searchParams.get('type') || '');
  const [filterLocation, setFilterLocation] = useState(searchParams.get('location') || '');
  const [filterFreeConsult, setFilterFreeConsult] = useState(searchParams.get('free') === 'true');
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const t = setTimeout(fetchProfessionals, 400);
    return () => clearTimeout(t);
  }, [filterType, filterFreeConsult, filterLocation]);

  useEffect(() => {
    // Update URL params
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (filterType) params.set('type', filterType);
    if (filterLocation) params.set('location', filterLocation);
    if (filterFreeConsult) params.set('free', 'true');
    setSearchParams(params);
  }, [searchQuery, filterType, filterLocation, filterFreeConsult]);

  const fetchProfessionals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('professional_type', filterType);
      if (filterLocation) params.append('location', filterLocation);
      if (filterFreeConsult) params.append('offers_free_consultation', 'true');
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`${API}/directory/professionals?${params.toString()}`);
      setProfessionals(response.data.professionals);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching professionals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProfessionals();
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="professional-directory-page">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition">
                <BrandLogo className="w-9 h-9" testId="directory-logo" />
                <span className="text-xl font-bold text-gray-800 hidden sm:inline">Legacy Forge Family</span>
              </Link>
              <span className="text-gray-300">|</span>
              <h1 className="text-lg font-bold text-gray-800">Professional Directory</h1>
            </div>
            
            <div className="flex items-center space-x-3">
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="px-4 py-2 text-gray-600 hover:text-blue-600 font-medium"
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-4 py-2 text-gray-600 hover:text-blue-600 font-medium"
                  >
                    Log In
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Find Trusted Family Advisors
          </h1>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto mb-8">
            Connect with vetted financial advisors, estate attorneys, family therapists, 
            and other professionals recommended by families like yours.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="flex bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="relative flex-1">
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, specialty, or location..."
                  className="w-full pl-12 pr-4 py-4 text-gray-800 focus:outline-none"
                  data-testid="search-input"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-4 bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                data-testid="search-btn"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b sticky top-16 z-30">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <FaFilter className="text-gray-400" />
              <span className="text-gray-600 font-medium">Filter by:</span>
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 bg-white"
              data-testid="type-filter"
            >
              <option value="">All Types</option>
              {Object.entries(PROFESSIONAL_TYPES).map(([key, { label, icon }]) => (
                <option key={key} value={key}>{icon} {label}</option>
              ))}
            </select>

            <input
              type="text"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              placeholder="Location..."
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
              data-testid="location-filter"
            />

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filterFreeConsult}
                onChange={(e) => setFilterFreeConsult(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                data-testid="free-consult-filter"
              />
              <span className="text-gray-700">Free Consultation</span>
            </label>

            <span className="ml-auto text-gray-500">
              {total} professional{total !== 1 ? 's' : ''} found
            </span>
            <RequestProButton
              testId="directory-request-pro-btn"
              initialSpecialty=""
              initialCity={filterLocation}
              className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 font-semibold transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading professionals...</p>
          </div>
        ) : professionals.length === 0 ? (
          <div className="py-6">
            <ComingSoonAdvisors
              typeKey={filterType}
              typeLabel={PROFESSIONAL_TYPES[filterType]?.label}
              location={filterLocation}
              testId="directory-coming-soon"
            />
            <div className="text-center mt-6">
              <Link
                to="/professionals/signup"
                className="inline-flex items-center text-blue-700 hover:underline font-medium"
                data-testid="directory-join-link"
              >
                <FaUserTie className="mr-2" />
                Are you a professional? Join our directory
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {professionals.map((prof) => (
              <ProfessionalCard
                key={prof.profile_id}
                professional={prof}
                onSelect={() => setSelectedProfessional(prof)}
              />
            ))}
          </div>
        )}

        {/* CTA for Professionals */}
        <div className="mt-12 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-8 text-center border border-blue-100">
          <h3 className="text-xl font-bold text-gray-800 mb-2">Are You a Professional?</h3>
          <p className="text-gray-600 mb-6">
            Join our network of trusted advisors and connect with families seeking your expertise.
          </p>
          <Link
            to="/professionals/signup"
            className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
          >
            <FaUserTie className="mr-2" />
            Apply to Join Directory
          </Link>
        </div>
      </main>

      {/* Professional Detail Modal */}
      {selectedProfessional && (
        <ProfessionalDetailModal
          professional={selectedProfessional}
          onClose={() => setSelectedProfessional(null)}
          isAuthenticated={isAuthenticated}
          onReported={() => { setSelectedProfessional(null); fetchProfessionals(); }}
        />
      )}
    </div>
  );
}

function ProfessionalCard({ professional, onSelect }) {
  const typeInfo = PROFESSIONAL_TYPES[professional.professional_type] || { 
    label: 'Professional', 
    icon: '👤', 
    color: 'gray' 
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-md hover:shadow-lg transition cursor-pointer overflow-hidden"
      onClick={onSelect}
      data-testid={`professional-card-${professional.profile_id}`}
    >
      {professional.offers_free_consultation && (
        <div className="bg-green-500 text-white text-xs font-bold px-4 py-1 text-center">
          FREE INITIAL CONSULTATION
        </div>
      )}

      {professional.pilot_participant ? (
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold px-4 py-1 text-center flex items-center justify-center gap-1" data-testid="pilot-pro-ribbon">
          <FaAward /> FOUNDING PILOT PROFESSIONAL
        </div>
      ) : professional.founding_advisor && (
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold px-4 py-1 text-center flex items-center justify-center gap-1">
          <FaAward /> FOUNDING ADVISOR
        </div>
      )}

      <div className="p-6">
        <div className="flex items-start space-x-4">
          <div className={`w-14 h-14 bg-${typeInfo.color}-100 rounded-full flex items-center justify-center flex-shrink-0 text-2xl`}>
            {typeInfo.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-800 truncate">{professional.name}</h3>
            <p className="text-sm text-gray-600">{typeInfo.label}</p>
            {professional.company && (
              <p className="text-sm text-gray-500 truncate">{professional.company}</p>
            )}
          </div>
        </div>

        {professional.bio && (
          <p className="text-gray-600 text-sm mt-4 line-clamp-2">{professional.bio}</p>
        )}
        {professional.bio && professional.bio.length > 100 && (
          <p className="text-blue-600 text-sm mt-1 font-medium hover:underline">View full profile &rarr;</p>
        )}

        {professional.specialties && professional.specialties.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {professional.specialties.slice(0, 3).map((specialty, i) => (
              <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                {specialty}
              </span>
            ))}
            {professional.specialties.length > 3 && (
              <span className="text-xs text-gray-500">+{professional.specialties.length - 3} more</span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <div className="flex items-center space-x-3">
            {professional.rating && (
              <div className="flex items-center text-amber-500">
                <FaStar className="mr-1" />
                <span className="font-medium">{professional.rating.toFixed(1)}</span>
              </div>
            )}
            {professional.years_experience && (
              <span className="text-gray-500 text-sm">
                {professional.years_experience}+ yrs
              </span>
            )}
          </div>
          {professional.location && (
            <div className="flex items-center text-gray-500 text-sm">
              <FaMapMarkerAlt className="mr-1" />
              <span className="truncate max-w-[100px]">{professional.location}</span>
            </div>
          )}
        </div>

        {professional.hourly_rate && (
          <div className="mt-3 text-sm">
            <span className="text-gray-500">From</span>{' '}
            <span className="font-bold text-gray-800">${professional.hourly_rate}/hr</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ProfessionalDetailModal({ professional, onClose, isAuthenticated, onReported }) {
  const navigate = useNavigate();
  const typeInfo = PROFESSIONAL_TYPES[professional.professional_type] || { 
    label: 'Professional', 
    icon: '👤' 
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="professional-detail-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white text-xl"
          >
            &times;
          </button>
          
          <div className="flex items-start space-x-4">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center text-3xl">
              {typeInfo.icon}
            </div>
            <div>
              <h2 className="text-2xl font-bold">{professional.name}</h2>
              <p className="text-blue-100">{typeInfo.label}</p>
              {professional.company && (
                <p className="text-blue-200 text-sm">{professional.company}</p>
              )}
              {professional.founding_advisor && (
                <div className="mt-2 inline-flex items-center gap-1 bg-amber-400/20 text-amber-200 text-xs font-bold px-3 py-1 rounded-full">
                  <FaAward /> Founding Advisor
                </div>
              )}
              {professional.pilot_participant && (
                <div className="mt-2 ml-2 inline-flex items-center gap-1 bg-emerald-400/20 text-emerald-200 text-xs font-bold px-3 py-1 rounded-full">
                  <FaAward /> Founding Pilot Professional
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          <ProfileDetails professional={professional} onReported={onReported} />
          <ShareProfileLink profileId={professional.profile_id} />

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Close
            </button>
            {isAuthenticated ? (
              <button
                onClick={() => navigate('/resources?tab=professionals')}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center"
              >
                <FaCalendarAlt className="mr-2" />
                Book Consultation
              </button>
            ) : (
              <button
                onClick={() => navigate('/register')}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Sign Up to Book
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
